import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { NodeAssociations, NodeUsers } from '@/components/nodes'
import { User, Project, Workspace, Document, Task } from '@/types'
import { LucideIcon } from 'lucide-react'
import { User as UserIcon, Users, Boxes, ClipboardList, FileText } from 'lucide-react'

export type AssociationType =
  | 'assignee'
  | 'collaborators'
  | 'workspace'
  | 'workspaces'
  | 'projects'
  | 'documents'
  | 'tasks'
  | 'users'

interface AssociationConfig {
  /** The type of association to display */
  type: AssociationType
  /** Configuration options for this association */
  options?: {
    /** Maximum number of items to show before "+X more" indicator */
    maxVisible?: number
    /** Size of avatars for user associations */
    avatarSize?: 'sm' | 'md' | 'lg'
    /** Whether to show "View all" button */
    showViewAll?: boolean
    /** Display mode for associations */
    displayMode?: 'badge' | 'text' | 'card'
    /** Custom label to display */
    label?: string
    /** Custom icon to display */
    icon?: React.ComponentType<{ className?: string }> | LucideIcon
    /** Whether to stack the label above the content */
    labelStacked?: boolean
    /** Whether to stack items vertically */
    listStacked?: boolean
    /** Whether to show user names next to avatars */
    showUserName?: boolean
  }
}

interface AssociationsCardProps {
  /** Title for the associations card */
  title?: string
  /** Array of association configurations */
  associations: AssociationConfig[]
  /** Data object containing the actual association data */
  data: {
    assignee?: User
    collaborators?: User[]
    workspace?: Workspace
    workspaces?: Workspace[]
    projects?: Project[]
    documents?: Document[]
    tasks?: Task[]
    users?: User[]
  }
  /** Additional CSS classes for the card */
  className?: string
}

// Helper function to get default options for each association type
function getDefaultOptions(type: AssociationType) {
  const defaults = {
    assignee: {
      maxVisible: 1,
      avatarSize: 'md' as const,
      showViewAll: false,
      label: 'Assignee',
      icon: UserIcon,
      labelStacked: true,
      showUserName: true,
    },
    collaborators: {
      avatarSize: 'md' as const,
      showViewAll: false,
      label: 'Collaborators',
      icon: Users,
      labelStacked: true,
    },
    workspace: {
      showViewAll: false,
      displayMode: 'text' as const,
      label: 'Workspaces',
      icon: Boxes,
      labelStacked: true,
      listStacked: true,
    },
    workspaces: {
      showViewAll: false,
      displayMode: 'text' as const,
      label: 'Workspaces',
      icon: Boxes,
      labelStacked: true,
      listStacked: true,
    },
    projects: {
      showViewAll: false,
      displayMode: 'text' as const,
      label: 'Projects',
      icon: ClipboardList,
      labelStacked: true,
      listStacked: true,
    },
    documents: {
      showViewAll: false,
      displayMode: 'card' as const,
      label: 'Documents',
      icon: FileText,
      labelStacked: true,
      listStacked: true,
    },
    users: {
      maxVisible: 5,
      avatarSize: 'sm' as const,
      label: 'Users',
      icon: Users,
      labelStacked: true,
    },
    tasks: {
      showViewAll: false,
      displayMode: 'card' as const,
      label: 'Tasks',
      icon: ClipboardList,
      labelStacked: true,
      listStacked: true,
    },
  }

  return defaults[type] || {}
}

// Helper function to render a single association based on type and config
function renderAssociation(config: AssociationConfig, data: AssociationsCardProps['data']) {
  const { type, options = {} } = config
  const mergedOptions = { ...getDefaultOptions(type), ...options }

  switch (type) {
    case 'assignee':
      if (!data.assignee) return null
      return (
        <NodeUsers
          key={type}
          users={[data.assignee]}
          maxVisible={mergedOptions.maxVisible}
          avatarSize={mergedOptions.avatarSize}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          labelStacked={mergedOptions.labelStacked}
          showUserName={mergedOptions.showUserName}
        />
      )

    case 'collaborators':
      if (!data.collaborators || data.collaborators.length === 0) return null
      return (
        <NodeUsers
          key={type}
          users={data.collaborators}
          maxVisible={mergedOptions.maxVisible}
          avatarSize={mergedOptions.avatarSize}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          labelStacked={mergedOptions.labelStacked}
          showUserName={mergedOptions.showUserName}
        />
      )

    case 'workspace':
      if (!data.workspace) return null
      return (
        <NodeAssociations
          key={type}
          nodes={[data.workspace]}
          nodeType="workspaces"
          maxVisible={mergedOptions.maxVisible}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          displayMode={mergedOptions.displayMode}
          labelStacked={mergedOptions.labelStacked}
          listStacked={mergedOptions.listStacked}
        />
      )

    case 'workspaces':
      if (!data.workspaces || data.workspaces.length === 0) return null
      return (
        <NodeAssociations
          key={type}
          nodes={data.workspaces}
          nodeType="workspaces"
          maxVisible={mergedOptions.maxVisible}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          displayMode={mergedOptions.displayMode}
          labelStacked={mergedOptions.labelStacked}
          listStacked={mergedOptions.listStacked}
        />
      )

    case 'projects':
      if (!data.projects || data.projects.length === 0) return null
      return (
        <NodeAssociations
          key={type}
          nodes={data.projects}
          nodeType="projects"
          maxVisible={mergedOptions.maxVisible}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          displayMode={mergedOptions.displayMode}
          labelStacked={mergedOptions.labelStacked}
          listStacked={mergedOptions.listStacked}
        />
      )

    case 'documents':
      if (!data.documents || data.documents.length === 0) return null
      return (
        <NodeAssociations
          key={type}
          nodes={data.documents}
          nodeType="documents"
          maxVisible={mergedOptions.maxVisible}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          displayMode={mergedOptions.displayMode}
          labelStacked={mergedOptions.labelStacked}
          listStacked={mergedOptions.listStacked}
        />
      )

    case 'users':
      if (!data.users || data.users.length === 0) return null
      return (
        <NodeUsers
          key={type}
          users={data.users}
          maxVisible={mergedOptions.maxVisible}
          avatarSize={mergedOptions.avatarSize}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          labelStacked={mergedOptions.labelStacked}
          showUserName={mergedOptions.showUserName}
        />
      )

    case 'tasks':
      if (!data.tasks || data.tasks.length === 0) return null
      return (
        <NodeAssociations
          key={type}
          nodes={data.tasks}
          nodeType="tasks"
          maxVisible={mergedOptions.maxVisible}
          showViewAll={mergedOptions.showViewAll}
          icon={mergedOptions.icon as LucideIcon}
          label={mergedOptions.label}
          displayMode={mergedOptions.displayMode}
          labelStacked={mergedOptions.labelStacked}
          listStacked={mergedOptions.listStacked}
        />
      )

    default:
      return null
  }
}

/**
 * AssociationsCard Component
 *
 * A reusable card component for displaying various associations
 * (users, workspaces, projects, documents, etc.) in a consistent layout.
 *
 * @example
 * ```tsx
 * // Basic usage with multiple association types
 * <AssociationsCard
 *   title="Associations"
 *   associations={[
 *     { type: 'assignee' },
 *     { type: 'workspace' },
 *     { type: 'projects' }
 *   ]}
 *   data={{
 *     assignee: task.assignee,
 *     workspace: task.workspace,
 *     projects: task.projects,
 *     collaborators: task.users.filter(u => u.id !== task.assignee?.id)
 *   }}
 * />
 *
 * // With custom options
 * <AssociationsCard
 *   title="Team Members"
 *   associations={[
 *     {
 *       type: 'users',
 *       options: {
 *         maxVisible: 10,
 *         avatarSize: 'sm',
 *         label: 'Team Members'
 *       }
 *     }
 *   ]}
 *   data={{
 *     users: project.users
 *   }}
 * />
 *
 * // For different node types with custom display modes
 * <AssociationsCard
 *   title="Project Associations"
 *   associations={[
 *     {
 *       type: 'collaborators',
 *       options: {
 *         maxVisible: 8,
 *         showUserName: true
 *       }
 *     },
 *     {
 *       type: 'documents',
 *       options: {
 *         displayMode: 'card',
 *         maxVisible: 5
 *       }
 *     }
 *   ]}
 *   data={{
 *     collaborators: project.users,
 *     documents: project.documents
 *   }}
 * />
 * ```
 */
export function AssociationsCard({
  title = 'Associations',
  associations,
  data,
  className = '',
}: AssociationsCardProps) {
  // Filter out associations that have no data
  const validAssociations = associations.filter((config) => {
    switch (config.type) {
      case 'assignee':
        return !!data.assignee
      case 'collaborators':
        return data.collaborators && data.collaborators.length > 0
      case 'workspace':
        return !!data.workspace
      case 'workspaces':
        return data.workspaces && data.workspaces.length > 0
      case 'projects':
        return data.projects && data.projects.length > 0
      case 'documents':
        return data.documents && data.documents.length > 0
      case 'users':
        return data.users && data.users.length > 0
      case 'tasks':
        return data.tasks && data.tasks.length > 0
      default:
        return false
    }
  })

  if (validAssociations.length === 0) {
    return null
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {validAssociations.map((config) => renderAssociation(config, data))}
        </div>
      </CardContent>
    </Card>
  )
}
